import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import Skeleton from '~/components/Exchange/components/Skeleton'
import { useTokenPriceWithUseAPI } from '~/hooks/useTokenPrice'
import { useSpotSwapPair } from '~/state/dex/hooks'
import { Currency, FixedPointNumber } from '~/utils/fixedPoint'
import { toBigNumber, formatFormAmount } from '~/utils/numbers'
import { Row, StyledDetailValue } from '../../styled'
import { useCalcSettlementFeeRisk } from '~/components/Exchange/hooks'
import { formatUnits } from 'viem'

interface Props {
  settlementFeeAmount: bigint
  loading: boolean
}

const SettlementFee: React.FC<Props> = ({
  settlementFeeAmount = 0n,
  loading,
}) => {
  const { t } = useTranslation()
  const [, outputToken] = useSpotSwapPair()
  const tokenPrice = useTokenPriceWithUseAPI({
    tokenAddress: outputToken?.address,
    chainId: outputToken?.chainId,
  })
  const settlementFee = formatUnits(settlementFeeAmount, outputToken?.decimals)
  const { level } = useCalcSettlementFeeRisk(settlementFeeAmount)

  const usdValue = toBigNumber(settlementFee).times(tokenPrice)

  const usdValueLiteral = useMemo(() => {
    if (!Number(usdValue)) {
      return '~'
    } else if (Number(usdValue) < 0.01) {
      return '< $0.01'
    } else {
      return `${new FixedPointNumber(usdValue).toCurrency(Currency.USD, 2)}`
    }
  }, [usdValue])
  const isZeroSettlementFee = settlementFee === '0'

  const valueLiteral = isZeroSettlementFee
    ? `0 ${outputToken?.symbol}`
    : Number(settlementFee)
    ? `${usdValueLiteral}(${formatFormAmount(settlementFee, 3)} ${
        outputToken?.symbol
      })`
    : '~'

  const detailLevel = level === 'info' ? 'info' : 'warning'

  return (
    <Row>
      <span>{t('settlement_fee')}</span>
      {loading ? (
        <Skeleton />
      ) : (
        <StyledDetailValue level={detailLevel}>
          {valueLiteral}
        </StyledDetailValue>
      )}
    </Row>
  )
}

export default SettlementFee
